/*
 *
 * MerchantSearch
 *
 * @version 1.0.0
 * @author Stefano Servodidio
 * @since 21-Nov-2024
 */

import React from 'react'
import PropTypes from 'prop-types'
import './MerchantSearch.scss'
import { Input } from '@mui/base/Input'
import { Box } from '@mui/system'
import { Dismiss, Search } from '@nexidigital/iconlibrary'
import PortalIconButton from '@nexidigital/portaliconbutton'

const MerchantSearch = React.forwardRef(function MerchantSearch(
  { onSearch, onClear, onChange, onKeyUp, onKeyDown, onFocus, onBlur, slotProps, className, sx, focused, ...props },
  ref
) {
  const isClearable = typeof onClear === 'function' && !!props.value
  const isFocused = focused || false

  return (
    <Box className={`merchantSearchWrapper ${className}`} {...{ sx }}>
      <Input
        className='merchantSearchRoot'
        slotProps={{
          ...slotProps,
          input: {
            onChange,
            onKeyUp: (e) => {
              onKeyUp(e)
              if (e.key === 'Enter') {
                e.preventDefault()
                onSearch(e, props.value)
              }
            },
            onKeyDown,
            onFocus,
            onBlur,
            ...slotProps?.input
          }
        }}
        {...props}
        ref={ref}
      />
      {(!isFocused || isClearable) && (
        <Box className='merchantSearchButton'>
          {isClearable ? (
            <PortalIconButton
              disabled={!!props.disabled}
              ariaLabel={props.cancelLabel}
              icon={<Dismiss size='24' type='line' />}
              onClick={(e) => {
                e?.stopPropagation?.()
                onClear(e)
              }}
            />
          ) : (
            <Search size='24' type='line' />
          )}
        </Box>
      )}
    </Box>
  )
})

MerchantSearch.propTypes = {
  /** aria-label */
  'aria-label': PropTypes.string.isRequired,
  /** pass custom classes */
  className: PropTypes.string,
  /** pass custom style */
  sx: PropTypes.object,
  /** launch search handler */
  onSearch: PropTypes.func,
  /** clear search handler */
  onClear: PropTypes.func,
  /** onChange input event handler */
  onChange: PropTypes.func,
  /** onKeyUp input event handler */
  onKeyUp: PropTypes.func,
  /** onKeyDown input event handler */
  onKeyDown: PropTypes.func,
  /** onFocus input event handler */
  onFocus: PropTypes.func,
  /** onBlur input event handler */
  onBlur: PropTypes.func,
  /** onBlur input event handler */
  slotProps: PropTypes.object,
  /** input value */
  value: PropTypes.string,
  /** input is focused */
  focused: PropTypes.bool,
  /** disable input */
  disabled: PropTypes.bool,
  /** cancel button label */
  cancelLabel: PropTypes.string
  // see https://mui.com/base-ui/react-input/components-api/ for other props
}

MerchantSearch.defaultProps = {
  className: '',
  onSearch: () => null,
  onClear: () => null,
  onChange: () => null,
  onKeyUp: () => null,
  onKeyDown: () => null,
  onFocus: () => null,
  onBlur: () => null,
  value: '',
  focused: false,
  disabled: false
}

export default MerchantSearch
